import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface CopyableValueProps {
  text: string;
  copyValue?: string;
  href?: string | null;
  copyTitle?: string;
  openTitle?: string;
  textSx?: SxProps<Theme>;
  iconFontSize?: string | number;
  revealOnHover?: boolean;
  copyButtonAlign?: 'left' | 'right';
}

export default function CopyableValue({
  text,
  copyValue,
  href,
  copyTitle = 'Copy',
  openTitle = 'Open in new tab',
  textSx,
  iconFontSize = '0.95rem',
  revealOnHover = false,
  copyButtonAlign = 'right',
}: CopyableValueProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyValue ?? text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // navigator.clipboard rejects in insecure contexts; nothing to recover here
    }
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const copyButtonLabel = copied ? 'Copied!' : copyTitle;

  const copyButton = (
    <Tooltip title={copyButtonLabel}>
      <IconButton
        className="copyable-value__copy-btn"
        size="small"
        onClick={handleCopy}
        aria-label={copyButtonLabel}
        sx={{
          p: 0.25,
          flexShrink: 0,
          ...(revealOnHover
            ? {
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 120ms ease-in-out',
                ...(copyButtonAlign === 'left' ? { right: '100%' } : { left: '100%' }),
              }
            : {}),
        }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: iconFontSize, color: 'success.main' }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: iconFontSize }} />
        )}
      </IconButton>
    </Tooltip>
  );

  const textNode = (
    <Box
      component="span"
      sx={{
        userSelect: 'text',
        wordBreak: revealOnHover ? 'normal' : 'break-all',
        ...textSx,
      }}
    >
      {text}
    </Box>
  );

  const openButton = href ? (
    <Tooltip title={openTitle}>
      <IconButton size="small" onClick={handleOpen} sx={{ p: 0.25, flexShrink: 0 }}>
        <OpenInNewIcon sx={{ fontSize: iconFontSize }} />
      </IconButton>
    </Tooltip>
  ) : null;

  if (revealOnHover) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          '&:hover .copyable-value__copy-btn, & .copyable-value__copy-btn:focus-visible': {
            opacity: 1,
          },
        }}
      >
        {textNode}
        {copyButton}
        {openButton}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        maxWidth: '100%',
      }}
    >
      {copyButtonAlign === 'left' && copyButton}
      {textNode}
      {copyButtonAlign === 'right' && copyButton}
      {openButton}
    </Box>
  );
}
